/* eslint-disable react/button-has-type */
import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import { useLabels, useRequestData } from 'wsm-common-data';
import { Popover, Overlay } from 'react-bootstrap';
import { CommonModal } from 'wsm-common-modal';
import { VEHICLE_STATUS_CONFIG } from '../../../global-constants';
import useMedia from '../../../hooks/useMedia';

const LocationStatus = ({ dealerName, status, vehicleUuid }) => {
	const labels = useLabels();
	const { deviceType } = useRequestData();
	const canHover = useMedia('(hover: hover) and (pointer: fine)', false);
	const [isOpenModal, setOpenModal] = useState(false);
	const [show, setShow] = useState(false);
	const triggerRef = useRef(null);
	const hideTimer = useRef(null);
	const descId = `vehicle-status-desc-${vehicleUuid}`;

	const isDesktop = deviceType === 'DESKTOP';
	const statusConfig = VEHICLE_STATUS_CONFIG[status] || null;

	if (!statusConfig) return null;

	const { tooltipLabelKey, iconClass, textLabelKey, modalAriaLabelKey } =
		statusConfig;
	const tooltipText = labels.get(tooltipLabelKey);
	const statusLabel = labels.get(textLabelKey);
	const statusHtml = statusLabel
		? statusLabel.replace('{{locationName}}', dealerName)
		: '';
	const plainLocationLabel = statusHtml.replace(/<[^>]+>/g, '');

	const toggleModal = () => setOpenModal((prev) => !prev);

	const openTooltip = () => {
		clearTimeout(hideTimer.current);
		setShow(true);
	};

	const hideTooltip = () => {
		setShow(false);
	};

	const scheduleClose = () => {
		clearTimeout(hideTimer.current);
		hideTimer.current = setTimeout(() => setShow(false), 150);
	};

	// Keyboard handler for trigger span
	const handleKeyDown = (e) => {
		if (e.key === 'Escape') {
			setShow(false);
		}
	};

	if (!canHover || !isDesktop) {
		return (
			<>
				<button
					type="button"
					className="btn-unstyled btn--tooltip"
					onClick={toggleModal}
					aria-label={plainLocationLabel}
				>
					<span>
						<i
							className={`ddc-icon ${iconClass} align-middle pr-2 text-muted`}
							aria-hidden="true"
						/>
						{/* eslint-disable-next-line react/no-danger */}
						<span
							dangerouslySetInnerHTML={{ __html: statusHtml }}
						/>
					</span>
				</button>
				<CommonModal
					title={plainLocationLabel}
					isOpen={isOpenModal}
					onClose={toggleModal}
					ariaLabel={labels.get(modalAriaLabelKey)}
				>
					<p>{tooltipText}</p>
				</CommonModal>
			</>
		);
	}

	return (
		<>
			<span
				ref={triggerRef}
				role="button"
				tabIndex="0"
				aria-label={plainLocationLabel}
				aria-describedby={descId}
				onFocus={openTooltip}
				onBlur={scheduleClose}
				onMouseOver={openTooltip}
				onMouseOut={scheduleClose}
				onKeyDown={handleKeyDown}
				data-testid="vehicle-status-location"
				className="pointer btn-unstyled"
			>
				<i
					className={`ddc-icon ${iconClass} align-middle pr-2 text-muted`}
					aria-hidden="true"
				/>
				{/* eslint-disable-next-line react/no-danger */}
				<span dangerouslySetInnerHTML={{ __html: statusHtml }} />
			</span>
			{/* Persistent SR-only description — always in the DOM, always referenced. */}
			<span id={descId} className="sr-only" aria-hidden>
				{tooltipText}
			</span>
			<Overlay
				show={show}
				target={triggerRef.current}
				placement="top"
				onHide={hideTooltip}
			>
				<Popover
					id={`vehicle-status-tooltip-${vehicleUuid}`}
					role="tooltip"
					className="inv-listing-vehicle-status-popover"
					onMouseOver={openTooltip}
					onMouseOut={scheduleClose}
				>
					<span>{tooltipText}</span>
				</Popover>
			</Overlay>
		</>
	);
};

LocationStatus.propTypes = {
	dealerName: PropTypes.string.isRequired,
	status: PropTypes.string.isRequired,
	vehicleUuid: PropTypes.string.isRequired
};

export default LocationStatus;
